<script lang="ts">
  import { storedColor } from '$lib/utils/colors';
  import { untrack } from 'svelte';
  import { parseIssueView, serializeIssueView } from '$lib/utils/issueViews';
  import { loadIssueSnapshot, sortIssues, parseIssueSort, updateIssuesIndividually } from '$lib/utils/issueList';
  import type { IssueSort, BulkIssueResult } from '$lib/utils/issueList';
  import { confirmAction } from '$lib/state/actionDialog.svelte';
  import { formatIST, relativeTime } from '$lib/utils/dates';
  import TcsNumber from '../ui/TcsNumber.svelte';
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { ChevronDown, MapPin, CircleAlert, Camera, X, Plus, Clock } from 'lucide-svelte';
  import {
    issuesApi,
    issueAttachmentsApi,
    peopleApi,
    assignableUsersApi
  } from '$lib/api/crm';
  import { session } from '$lib/state/session.svelte';
  import { toast } from '$lib/state/toast.svelte';
  import { route } from '$lib/state/route.svelte';
  import { apiFetch, getErrorMessage } from '$lib/api/client';
  import Pagination from '$lib/components/ui/Pagination.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import CustomFieldsForm from '$lib/components/ui/CustomFieldsForm.svelte';
  import UnitCombobox from '$lib/components/ui/UnitCombobox.svelte';
  import CompanyCombobox from '$lib/components/ui/CompanyCombobox.svelte';
  import FilterChipRow from '$lib/components/ui/FilterChipRow.svelte';
  import IssueDetailPanel from './IssueDetailPanel.svelte';
  import type { AssignableUser, CompanyLookup, Issue, IssueStatus } from '$lib/types';
  import { requiresResolution, resolutionError, resolutionHint } from '$lib/utils/issueResolution';

  const queryClient = useQueryClient();

  const isIssuesOnly = $derived(session.me?.user.issues_only ?? false);

  const statusDefs: Array<{ value: IssueStatus | ''; label: string; dot?: string }> = [
    { value: '', label: 'All' },
    { value: 'open', label: 'Open', dot: '#7AB000' },
    { value: 'in_progress', label: 'In progress', dot: '#D08700' },
    { value: 'resolved', label: 'Resolved', dot: '#666666' }
  ];
  const STATUS_BADGE: Record<string, { bg: string; fg: string }> = {
    open: { bg: '#ECF8CC', fg: '#5c7a00' },
    in_progress: { bg: '#FBF0DC', fg: '#8a5c00' },
    resolved: { bg: '#E6E9EE', fg: '#10223A' }
  };
  function statusBadge(s: string) {
    return STATUS_BADGE[s] ?? STATUS_BADGE.open;
  }
  function statusLabel(s: string) {
    return statusDefs.find((c) => c.value === s)?.label ?? s;
  }
  // ponytail: no backend per-vertical color — cycle a small brand-safe
  // palette by list position, same trick Header.svelte uses.
  const VERT_DOTS = ['#97D700', '#226A73', '#D08700', '#5F9CA3'];

  let search = $state('');
  const categories = [
    { value: 'issue', label: 'Issue / complaint' },
    { value: 'customer_feedback', label: 'Customer feedback' },
    { value: 'customer_log', label: 'Customer conversation' }
  ];
  let category = $state('');
  let mine = $state(false);
  let overdue = $state(false);
  const followupFilters = $derived({ category: category || undefined, assignee_user_id: mine ? 'mine' : undefined, next_action_due: overdue ? 'overdue' : undefined });
  let page = $state(1);
  let status = $state<IssueStatus | ''>('');
  let companyFilter = $state<number | undefined>(undefined);
  let unitFilter = $state<string | undefined>(undefined);
  let companyMenuOpen = $state(false);
  let unitMenuOpen = $state(false);
  let showForm = $state(false);
  let editingId = $state<number | null>(null);
  let originalStatus = $state<IssueStatus | undefined>(undefined);
  let formError = $state('');
  let view = $state<'list' | 'board'>('list');
  let sort = $state<IssueSort>('id_desc');
  let dragId = $state<number | null>(null);
  // Set right before navigating to the detail panel from a board drop onto
  // "Resolved" — tells IssueDetailPanel to open pre-armed with the resolution form
  // instead of a bare row click, so the resolve gate is never bypassed. See
  // closeDetail/onDropStatus below.
  let boardResolveArm = $state<number | null>(null);

  // Selected issue lives in the URL hash (#/issues/<id>) so Back/Forward and
  // refresh both work, same convention as Companies/Leads detail views.
  const selectedIssueId = $derived(route.current.tab === 'issues' ? (route.current.id ?? null) : null);

  const supportTeams = $derived(session.me?.issue_verticals ?? session.me?.verticals ?? []);
  let supportSelection = $state<number | 'all' | null>(null);
  let savedViewName = $state('');
  let savedViews = $state<{ name: string; filters: Record<string, string> }[]>([]);
  const savedViewKey = $derived(`toyama:issue-views:${session.me?.user.id ?? 'anonymous'}`);
  $effect(() => {
    const key = savedViewKey;
    try {
      const stored = JSON.parse(localStorage.getItem(key) || '[]');
      savedViews = Array.isArray(stored) ? stored.filter(v => typeof v?.name === 'string' && v?.filters && typeof v.filters === 'object').map(v => ({ name: v.name, filters: serializeIssueView(parseIssueView(v.filters)) })) : [];
    } catch { savedViews = []; }
  });
  $effect(() => {
    if (route.current.tab !== 'issues') return;
    const filters = route.current.filters;
    untrack(() => {
      const parsed = parseIssueView(filters);
      search = parsed.q; category = parsed.category; status = parsed.status;
      mine = parsed.mine; overdue = parsed.overdue; companyFilter = parsed.company;
      unitFilter = parsed.unit; view = parsed.view; supportSelection = parsed.team ?? null;
      sort = parseIssueSort(parsed.sort);
    });
  });
  $effect(() => {
    const filters = serializeIssueView({ q: search, category, status, mine, overdue, company: companyFilter, unit: unitFilter, view, team: supportSelection ?? undefined, sort });
    untrack(() => {
      if (route.current.tab === 'issues' && JSON.stringify(filters) !== JSON.stringify(route.current.filters ?? {})) route.replace({ ...route.current, filters });
    });
  });
  function saveView() {
    const name = savedViewName.trim();
    if (!name) return;
    const next = [...savedViews.filter(v => v.name !== name), { name, filters: serializeIssueView(parseIssueView(route.current.filters)) }];
    try { localStorage.setItem(savedViewKey, JSON.stringify(next)); savedViews = next; savedViewName = ''; toast.success('View saved on this device'); }
    catch { toast.error('Unable to save this view on this device'); }
  }
  function removeSavedView(name: string) {
    const next = savedViews.filter(v => v.name !== name);
    try { localStorage.setItem(savedViewKey, JSON.stringify(next)); savedViews = next; }
    catch { toast.error('Unable to remove saved view'); }
  }
  const defaultSupportTeam = $derived(session.selectedVertical === 'all' && supportTeams.length > 1 ? 'all' : supportTeams.some((v) => v.id === session.selectedVertical) ? session.selectedVertical : supportTeams[0]?.id ?? 'all');
  const activeSupportTeam = $derived.by(() => {
    if (supportSelection === 'all' && supportTeams.length > 1) return 'all';
    if (typeof supportSelection === 'number' && supportTeams.some((v) => v.id === supportSelection)) return supportSelection;
    return defaultSupportTeam;
  });
  const verticalId = $derived(activeSupportTeam === 'all' ? undefined : activeSupportTeam);
  // createQuery evaluates its options immediately, even when disabled.
  // Initialize the form after its team dependencies but before picker queries.
  let form = $state(emptyForm());
  function hasCRMAccess(id: number | undefined) {
    return id !== undefined && !!session.me?.verticals.some((v) => v.id === id);
  }
  const filtersActive = $derived(!!status || !!category || mine || overdue || companyFilter !== undefined || unitFilter !== undefined);
  function clearFilters() {
    category = '';
    mine = false;
    overdue = false;
    status = '';
    companyFilter = undefined;
    unitFilter = undefined;
  }

  // Cheap per-status totals (per_page:1 is enough — `count` already reflects
  // the server-side filtered total), same trick LeadsPage's tab counts use.
  const statusCounts = statusDefs.map((s) => ({
    value: s.value,
    query: createQuery(() => ({
      queryKey: ['issues-count', { status: s.value, companyFilter, unitFilter, verticalId, followupFilters }],
      queryFn: () =>
        issuesApi.list({
          page: 1,
          per_page: 1,
          ...followupFilters,
          status: s.value || undefined,
          company_id: companyFilter,
          unit: unitFilter,
          vertical_id: verticalId
        })
    }))
  }));
  function countFor(value: string) {
    return statusCounts.find((s) => s.value === value)?.query.data?.count ?? 0;
  }
  const statusChips = $derived(
    statusDefs.map((s) => ({ id: s.value, label: s.label, dot: s.dot, count: countFor(s.value) }))
  );

  const listQuery = createQuery(() => ({
    queryKey: ['issues', { search, page, status, companyFilter, unitFilter, verticalId, followupFilters }],
    queryFn: () =>
      issuesApi.list({
        q: search || undefined,
        page,
        per_page: 25,
        ...followupFilters,
        status: status || undefined,
        company_id: companyFilter,
        unit: unitFilter,
        vertical_id: verticalId
      }),
    enabled: view === 'list' && sort === 'id_desc'
  }));
  const sortedQuery = createQuery(() => ({
    queryKey: ['issues', 'sorted', { search, status, companyFilter, unitFilter, verticalId, followupFilters }],
    queryFn: () => {
      // Freeze the scope/filter input for every page in this snapshot, even
      // if the user changes filters while earlier requests are in flight.
      const params = { q: search || undefined, ...followupFilters, status: status || undefined, company_id: companyFilter, unit: unitFilter, vertical_id: verticalId };
      return loadIssueSnapshot((page, per_page) => issuesApi.list({ ...params, page, per_page }));
    },
    enabled: view === 'list' && sort !== 'id_desc'
  }));
  const sortedRows = $derived(sortIssues(sortedQuery.data ?? [], sort, assigneeName));
  const listData = $derived(sort === 'id_desc' ? listQuery.data : sortedQuery.data ? { results: sortedRows.slice((page - 1) * 25, page * 25), count: sortedRows.length, per_page: 25, page } : undefined);
  const listLoading = $derived(sort === 'id_desc' ? listQuery.isPending : sortedQuery.isPending);
  const listError = $derived(sort === 'id_desc' ? listQuery.error : sortedQuery.error);
  function retryList() { if (sort === 'id_desc') listQuery.refetch(); else sortedQuery.refetch(); }
  function setSort(field: string) { sort = parseIssueSort(`${field}_${sort === `${field}_asc` ? 'desc' : 'asc'}`); page = 1; }
  function sortAria(field: string): 'ascending' | 'descending' | 'none' { return sort === `${field}_asc` ? 'ascending' : sort === `${field}_desc` ? 'descending' : 'none'; }

  let selection = $state<Map<number, Issue>>(new Map());
  let bulkMode = $state<'assign' | 'next' | 'status'>('assign');
  let bulkAssignee = $state<number | null>(null);
  let bulkNextAction = $state(''), bulkNextDate = $state('');
  let bulkStatus = $state<IssueStatus>('in_progress');
  let bulkTechnician = $state(''), bulkRoot = $state(''), bulkCorrective = $state(''), bulkPreventive = $state('');
  let bulkError = $state('');
  let bulkConfirming = $state(false);
  let selectionGeneration = 0;
  let bulkResult = $state<BulkIssueResult | null>(null);
  const selectedRows = $derived([...selection.values()]);
  $effect(() => {
    search; status; companyFilter; unitFilter; verticalId; followupFilters; view;
    untrack(() => { selectionGeneration++; selection = new Map(); bulkResult = null; bulkError = ''; });
  });
  function toggleSelection(row: Issue) {
    if (bulkMutation.isPending) return;
    const next = new Map(selection);
    if (next.has(row.id)) next.delete(row.id); else next.set(row.id, row);
    selection = next;
  }
  const bulkMutation = createMutation(() => ({
    mutationFn: (input: { rows: Issue[]; body: Record<string, unknown> }) => updateIssuesIndividually(input.rows, input.body, issuesApi.update, getErrorMessage),
    onSuccess: result => {
      bulkResult = result;
      const succeeded = new Set(result.succeeded);
      selection = new Map([...selection].filter(([id]) => !succeeded.has(id)));
      for (const key of ['issues', 'issues-board', 'issues-count', 'issue-detail', 'customer-care', 'my-day-issues']) queryClient.invalidateQueries({ queryKey: [key] });
      if (result.failed.length) toast.error(`${result.succeeded.length} updated; ${result.failed.length} failed. Successful changes were not rolled back.`);
      else toast.success(`${result.succeeded.length} issues updated`);
    }
  }));
  async function applyBulk() {
    if (isIssuesOnly || !selectedRows.length || bulkMutation.isPending || bulkConfirming) return;
    bulkError = '';
    let body: Record<string, unknown>;
    if (bulkMode === 'assign') {
      if (bulkAssignee !== null && !commonAssignees.some(user => user.id === bulkAssignee)) { bulkError = 'Choose an assignee who can work in every selected team, or clear assignment.'; return; }
      body = { assignee_user_id: bulkAssignee };
    } else if (bulkMode === 'next') {
      if (!bulkNextAction.trim() || !bulkNextDate) { bulkError = 'Enter a next action and its date.'; return; }
      body = { next_action: bulkNextAction.trim(), next_action_date: bulkNextDate };
    } else {
      body = { status: bulkStatus, ...(bulkStatus === 'resolved' ? { resolving_technician_name: bulkTechnician.trim(), capa_root_cause: bulkRoot.trim(), capa_corrective_action: bulkCorrective.trim(), capa_preventive_action: bulkPreventive.trim() } : {}) };
      for (const row of selectedRows) {
        const error = resolutionError(row.status, { status: bulkStatus, category: row.category, resolving_technician_name: bulkTechnician, capa_root_cause: bulkRoot, capa_corrective_action: bulkCorrective, capa_preventive_action: bulkPreventive });
        if (error) { bulkError = `Issue #${row.id}: ${error}`; return; }
      }
    }
    const rows = [...selectedRows];
    const generation = selectionGeneration;
    const actionLabel = bulkMode === 'assign' ? bulkAssignee === null ? 'clear assignment' : `assign to “${commonAssignees.find(user => user.id === bulkAssignee)?.name}”` : bulkMode === 'next' ? `set next action “${bulkNextAction.trim()}” due ${bulkNextDate}` : `set status “${statusLabel(bulkStatus)}”`;
    bulkConfirming = true;
    let accepted: boolean;
    try { accepted = await confirmAction(`${actionLabel.charAt(0).toUpperCase() + actionLabel.slice(1)} for ${rows.length} selected issues? Each is saved separately; failures do not roll back successful updates.${bulkStatus === 'resolved' && bulkMode === 'status' ? ' The same resolution evidence will be applied to all selected cases. This does not confirm customer satisfaction.' : ''}`); }
    finally { bulkConfirming = false; }
    if (!accepted || generation !== selectionGeneration) return;
    bulkResult = null;
    bulkMutation.mutate({ rows, body });
  }

  // Board view needs every issue across all three statuses at once to fill
  // columns, not one status/page at a time — same cap-at-300 trick as
  // DealsPage's boardQuery (comfortably covers this app's scale; a vertical
  // that outgrows it should switch to per-column pagination instead of
  // raising the cap further).
  const boardQuery = createQuery(() => ({
    queryKey: ['issues-board', { search, companyFilter, unitFilter, verticalId, followupFilters }],
    queryFn: () =>
      issuesApi.list({
        q: search || undefined,
        per_page: 300,
        ...followupFilters,
        company_id: companyFilter,
        unit: unitFilter,
        vertical_id: verticalId
      }),
    enabled: view === 'board'
  }));

  // Issue-safe lookups include support-only teams without widening CRM access.
  const companiesQuery = createQuery(() => ({
    queryKey: ['issue-companies-lookup', { verticalId }],
    queryFn: () => apiFetch<{ results: CompanyLookup[] }>(`/issue-lookups/companies${verticalId ? `?vertical_id=${verticalId}` : ''}`)
  }));
  const peopleQuery = createQuery(() => ({
    queryKey: ['people-picker', { verticalId: form.vertical_id }],
    queryFn: () => peopleApi.list({ per_page: 100, vertical_id: form.vertical_id }),
    enabled: showForm && !isIssuesOnly && hasCRMAccess(form.vertical_id)
  }));
  const assignableQuery = createQuery(() => ({
    queryKey: ['assignable-users'],
    queryFn: () => assignableUsersApi.list(),
    enabled: !isIssuesOnly
  }));
  const commonAssignees = $derived((assignableQuery.data?.results ?? []).filter(user => selectedRows.every(row => user.vertical_ids?.includes(row.vertical_id))));
  const formAssignableQuery = createQuery(() => ({
    queryKey: ['assignable-users', form.vertical_id],
    queryFn: () => apiFetch<{ results: AssignableUser[] }>(`/users/assignable?vertical_id=${form.vertical_id}`),
    enabled: showForm && !isIssuesOnly && form.vertical_id !== undefined
  }));
  const formCompaniesQuery = createQuery(() => ({
    queryKey: ['issue-companies-lookup', { verticalId: form.vertical_id }],
    queryFn: () => apiFetch<{ results: CompanyLookup[] }>(`/issue-lookups/companies?vertical_id=${form.vertical_id}`),
    enabled: showForm && form.vertical_id !== undefined
  }));

  const unitFilterOptions = createQuery(() => ({
    queryKey: ['issue-units', companyFilter],
    queryFn: () => issuesApi.units(companyFilter as number),
    enabled: companyFilter !== undefined
  }));

  $effect(() => {
    search;
    followupFilters;
    status;
    companyFilter;
    unitFilter;
    verticalId;
    sort;
    page = 1;
  });
  // Clearing the company filter also clears any unit filter that depended
  // on it — an orphaned unit filter for no company would silently 404-empty.
  $effect(() => {
    if (companyFilter === undefined) unitFilter = undefined;
  });

  type StagedPhoto = { file: File; previewUrl: string };
  function emptyForm() {
    return {
      vertical_id: verticalId ?? supportTeams[0]?.id,
      title: '',
      category: 'issue' as Issue['category'],
      next_action: '',
      next_action_date: '',
      reporter_name: '',
      reporter_phone: '',
      reporter_email: '',
      capa_root_cause: '',
      resolving_technician_name: '',
      capa_corrective_action: '',
      capa_preventive_action: '',
      company_id: undefined as number | undefined,
      unit: '',
      room: '',
      site_type: '' as NonNullable<Issue['site_type']>,
      location: '',
      customer_satisfaction: '' as NonNullable<Issue['customer_satisfaction']>,
      person_id: undefined as number | undefined,
      assignee_user_id: undefined as number | undefined,
      status: 'open' as IssueStatus,
      body: '',
      custom: {} as Record<string, unknown>
    };
  }
  const customFieldDefs = $derived(session.customFieldsFor('issues', form.vertical_id));
  let stagedPhotos = $state<StagedPhoto[]>([]);

  // Kept in sync by CompanyCombobox, including a newly created company.
  let selectedCompanyKind = $state<CompanyLookup['kind'] | undefined>(undefined);
  // Set by openEdit from the row's own company_name (see issueJSON on the
  // backend) — CompanyCombobox only resolves its initial display text
  // against the `companies` pool it's handed (companiesQuery, capped at 200
  // rows), so editing an issue whose company sorts past that cap would
  // otherwise render the picker blank despite form.company_id being correct
  // (same underlying cap as the list/board/panel bug, just in the write
  // path — see formCompanies below, which patches it in).
  let editingCompanyName = $state<string | undefined>(undefined);
  // companiesQuery.data plus, if missing, a synthetic single-row entry for
  // the issue currently being edited — so CompanyCombobox can show its name
  // even when the real row didn't make the capped lookup. Only ever used to
  // resolve the initial display text; picking a different company still
  // goes through the combobox's own live ?q= search.
  const formCompanies = $derived.by(() => {
    const base = formCompaniesQuery.data?.results ?? [];
    if (form.company_id === undefined || !editingCompanyName) return base;
    if (base.some((c) => c.id === form.company_id)) return base;
    const synthetic: CompanyLookup = {
      id: form.company_id,
      name: editingCompanyName,
      kind: selectedCompanyKind ?? 'company',
      city: '',
      vertical_id: form.vertical_id ?? 0
    };
    return [...base, synthetic];
  });
  // Do not infer a hotel from the team: only an explicitly selected hotel.
  const formDefaultCompanyKind = $derived(
    selectedCompanyKind === 'hotel' ? 'hotel' : 'company'
  );

  const formUnitsQuery = createQuery(() => ({
    queryKey: ['issue-units', form.company_id],
    queryFn: () => issuesApi.units(form.company_id as number),
    enabled: form.company_id !== undefined
  }));

  const saveMutation = createMutation(() => ({
    mutationFn: async (body: Record<string, unknown>) => {
      const saved = editingId ? await issuesApi.update(editingId, body) : await issuesApi.create(body);
      for (const p of stagedPhotos) {
        await issueAttachmentsApi.upload(saved.id, p.file);
      }
      return saved;
    },
    onSuccess: () => {
      if (editingId !== null) {
        queryClient.invalidateQueries({ queryKey: ['issue-detail', editingId] });
        queryClient.invalidateQueries({ queryKey: ['issue-comments', editingId] });
      }
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      queryClient.invalidateQueries({ queryKey: ['issues-count'] });
      queryClient.invalidateQueries({ queryKey: ['issues-board'] });
      closeForm();
    },
    onError: (err: unknown) => (formError = getErrorMessage(err))
  }));

  // Board drag-and-drop into Open/In progress (or back out of Resolved) —
  // a plain status change, no CAPA involved. Dropping *into* Resolved is
  // handled separately by onDropStatus, which never calls this for that case.
  const statusMutation = createMutation(() => ({
    mutationFn: ({ id, status: s, vertical_id }: { id: number; status: IssueStatus; vertical_id: number }) => issuesApi.update(id, { status: s, vertical_id }),
    onSuccess: (_, input) => {
      queryClient.invalidateQueries({ queryKey: ['issue-detail', input.id] });
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      queryClient.invalidateQueries({ queryKey: ['issues-count'] });
      queryClient.invalidateQueries({ queryKey: ['issues-board'] });
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  const BOARD_STATUSES: IssueStatus[] = ['open', 'in_progress', 'resolved'];
  const boardColumns = $derived.by(() =>
    BOARD_STATUSES.map((s) => ({
      status: s,
      cards: (boardQuery.data?.results ?? []).filter((row) => row.status === s)
    }))
  );

  // Mobile board: the three columns can't sit side by side on a phone, so
  // below `sm` a chip row picks one status and that column fills the width.
  // Same idea as DealsPage's stage focus; the desktop board is untouched.
  let focusStatusPick = $state<IssueStatus | null>(null);
  const focusStatus = $derived(focusStatusPick ?? BOARD_STATUSES[0]);
  const focusColumn = $derived(boardColumns.find((c) => c.status === focusStatus));
  const focusChips = $derived(
    boardColumns.map((c) => ({
      id: c.status,
      label: statusLabel(c.status),
      dot: statusBadge(c.status).fg,
      count: c.cards.length
    }))
  );

  function onDragStart(id: number) {
    if (isIssuesOnly) return;
    dragId = id;
  }
  function onDragEnd() {
    dragId = null;
  }
  // Every resolution requires the technician name; complaints also need CAPA.
  // A drop onto the Resolved column never mutates directly:
  // it arms boardResolveArm and opens the existing detail panel, which
  // (via the autoResolve prop) opens straight into its resolution form — same
  // form IssueDetailPanel's status pill uses, no duplicated UI. Any other
  // drop (including out of Resolved) is a normal status change.
  function onDropStatus(newStatus: IssueStatus) {
    if (isIssuesOnly) return;
    if (dragId === null) return;
    const id = dragId;
    dragId = null;
    const card = boardQuery.data?.results.find((row) => row.id === id);
    if (!card || card.status === newStatus) return;
    if (requiresResolution(card.status, newStatus)) {
      boardResolveArm = id;
      route.push({ tab: 'issues', id });
      return;
    }
    statusMutation.mutate({ id, status: newStatus, vertical_id: card.vertical_id });
  }

  function openCreate(prefill?: Record<string, unknown>) {
    editingId = null;
    originalStatus = undefined;
    form = { ...emptyForm(), ...prefill };
    selectedCompanyKind = companiesQuery.data?.results.find((c) => c.id === form.company_id)?.kind;
    editingCompanyName = undefined;
    formError = '';
    clearStagedPhotos();
    showForm = true;
  }

  function openEdit(row: Issue) {
    editingId = row.id;
    originalStatus = row.status;
    form = {
      vertical_id: row.vertical_id,
      title: row.title,
      category: row.category,
      next_action: row.next_action,
      next_action_date: row.next_action_date?.slice(0, 10) ?? '',
      reporter_name: row.reporter_name,
      reporter_phone: row.reporter_phone,
      reporter_email: row.reporter_email,
      capa_root_cause: row.capa_root_cause,
      resolving_technician_name: row.resolving_technician_name ?? '',
      capa_corrective_action: row.capa_corrective_action,
      capa_preventive_action: row.capa_preventive_action,
      company_id: row.company_id ?? undefined,
      unit: row.unit ?? '',
      room: row.room ?? '',
      site_type: row.site_type ?? '',
      location: row.location ?? '',
      customer_satisfaction: row.customer_satisfaction ?? '',
      person_id: row.person_id ?? undefined,
      assignee_user_id: row.assignee_user_id ?? undefined,
      status: row.status,
      body: row.body,
      custom: { ...row.custom }
    };
    selectedCompanyKind = companiesQuery.data?.results.find((c) => c.id === form.company_id)?.kind;
    editingCompanyName = row.company_name ?? undefined;
    formError = '';
    clearStagedPhotos();
    showForm = true;
  }

  function clearStagedPhotos() {
    for (const p of stagedPhotos) URL.revokeObjectURL(p.previewUrl);
    stagedPhotos = [];
  }

  function closeForm() {
    showForm = false;
    clearStagedPhotos();
  }

  let photoInput: HTMLInputElement | undefined = $state();
  function onPhotoChosen(e: Event) {
    const file = (e.currentTarget as HTMLInputElement).files?.[0];
    if (file) stagedPhotos = [...stagedPhotos, { file, previewUrl: URL.createObjectURL(file) }];
    (e.currentTarget as HTMLInputElement).value = '';
  }
  function removeStagedPhoto(i: number) {
    URL.revokeObjectURL(stagedPhotos[i].previewUrl);
    stagedPhotos = stagedPhotos.filter((_, idx) => idx !== i);
  }

  function submit(e: SubmitEvent) {
    e.preventDefault();
    if (!form.title.trim()) {
      formError = 'Title is required';
      return;
    }
    formError = isIssuesOnly && requiresResolution(originalStatus, form.status)
      ? 'Field technicians cannot mark cases resolved. Report completion to the team coordinator.'
      : resolutionError(originalStatus, form);
    if (formError) return;
    // Source-team keys retained after handover are history, not editable fields
    // in the destination. Send only the destination's defined custom fields.
    const allowedKeys = new Set(customFieldDefs.map((field) => field.key));
    const body: Record<string, unknown> = {
      ...form,
      resolving_technician_name: form.resolving_technician_name.trim(),
      company_id: form.company_id ?? null,
      person_id: form.person_id ?? null,
      assignee_user_id: form.assignee_user_id ?? null,
      custom: Object.fromEntries(Object.entries(form.custom).filter(([key]) => allowedKeys.has(key))),
      next_action_date: form.next_action_date || null
    };
    if (isIssuesOnly) {
      delete body.resolving_technician_name;
      delete body.person_id;
      delete body.assignee_user_id;
      delete body.category;
      delete body.next_action;
      delete body.next_action_date;
      delete body.customer_satisfaction;
      body.reporter_name = session.me?.user.name || session.me?.user.email;
      body.reporter_email = session.me?.user.email;
    }
    saveMutation.mutate(body);
  }

  // Only backs the filter-bar company chip label (a plain id, no Issue row
  // to read company_name off there). Every row-level company display uses
  // row.company_name directly (see issueJSON on the backend) — it's resolved
  // from company_id server-side, so unlike this lookup-based helper it isn't
  // capped at the companies-lookup picker's 200 rows (prod: a company past
  // that cap rendered no name anywhere — see propertyLine's note below).
  function companyName(id: number | null | undefined): string {
    if (!id) return '';
    return companiesQuery.data?.results.find((c) => c.id === id)?.name ?? '';
  }
  // Second muted line under the company name: location context, shown when
  // the company is a hotel (rooms live under a unit type there) or the
  // issue itself carries a unit/room — omitting whichever part is missing,
  // and the whole line when both are empty (a plain non-hotel company with
  // no unit/room has nothing to add here). kind still comes from the capped
  // companies-lookup list (there's no cheaper source for it), so a company
  // beyond that cap resolves to kind === undefined here — same branch as a
  // known non-hotel company, i.e. "show the line only when unit/room is
  // present", which is the practical case anyway.
  function propertyLine(row: Issue): string {
    const kind = companiesQuery.data?.results.find((c) => c.id === row.company_id)?.kind;
    if (kind !== 'hotel' && !row.unit && !row.room && !row.location) return '';
    const parts: string[] = [];
    if (row.unit) parts.push(row.unit);
    if (row.room) parts.push(row.room);
    if (row.location) parts.push(row.location);
    return parts.join(' · ');
  }
  function assigneeName(id: number | null): string {
    if (!id) return '';
    return assignableQuery.data?.results.find((u) => u.id === id)?.name ?? '';
  }
  function initialsOf(name: string): string {
    return (
      name
        .trim()
        .split(/\s+/)
        .map((s) => s[0])
        .filter(Boolean)
        .slice(0, 2)
        .join('')
        .toUpperCase() || '?'
    );
  }
  function vertLabel(id: number): { name: string; dot: string } {
    return {
      name: supportTeams.find((v) => v.id === id)?.name ?? '',
      dot: storedColor(supportTeams.find(v => v.id === id)?.settings?.color)
    };
  }
  function timeAgo(iso: string): string {
    const ms = Date.now() - new Date(iso).getTime();
    const min = Math.floor(ms / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const days = Math.floor(hr / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(iso).toLocaleDateString();
  }

  function dueLabel(date: string): string {
    return new Date(`${date.slice(0, 10)}T00:00:00Z`).toLocaleDateString(undefined, { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric' });
  }
  function dueBadge(date: string): string {
    const day = date.slice(0, 10);
    const today = new Date().toISOString().slice(0, 10);
    return day < today ? 'Overdue' : day === today ? 'Today' : '';
  }

  function openDetail(row: Issue) {
    route.push({ tab: 'issues', id: row.id });
  }
  function closeDetail() {
    boardResolveArm = null;
    route.push({ tab: 'issues' });
  }

  // Consumes the Header's "+ New issue" signal, and the "Log as issue"
  // signal from LeadDetail (arrives with a `prefill` payload — see
  // session.svelte.ts's PageSignal doc).
  $effect(() => {
    const signal = session.consumeSignal('issues');
    if (signal?.mode === 'create') openCreate(signal.prefill);
  });
</script>

<div class="tym-page flex h-full min-h-0 flex-col">
  <!-- filter bar -->
  <div class="tym-filter-bar mb-3.5 shrink-0">
    <select class="input !w-auto" aria-label="Support team" value={activeSupportTeam} onchange={(e) => { supportSelection = e.currentTarget.value === 'all' ? 'all' : Number(e.currentTarget.value); companyFilter = undefined; unitFilter = undefined; }}>
      {#if supportTeams.length > 1}<option value="all">All support teams</option>{/if}
      {#each supportTeams as v (v.id)}<option value={v.id}>{v.name}</option>{/each}
    </select>
    <select class="input !w-auto" aria-label="Filter category" bind:value={category}>
      <option value="">All categories</option>
      {#each categories as c}<option value={c.value}>{c.label}</option>{/each}
    </select>
    <button type="button" class="tym-chip" class:active={mine} onclick={() => (mine = !mine)}>Assigned to me</button>
    <button type="button" class="tym-chip" class:active={overdue} onclick={() => (overdue = !overdue)}>Overdue</button>
    <!-- the status tabs filter a single list; board view already shows all
         three statuses side by side, so they're meaningless there (same
         idea as DealsPage hiding its stage chips in board mode). -->
    {#if view === 'list'}
      <FilterChipRow
        chips={statusChips}
        activeId={status}
        onSelect={(id) => (status = id as IssueStatus | '')}
        ariaLabel="Filter issues by status"
      />

      <span class="tym-filter-divider hidden sm:block"></span>
    {/if}

    <div class="relative">
      <button
        type="button"
        class="tym-chip"
        class:active={companyFilter !== undefined}
        onclick={() => (companyMenuOpen = !companyMenuOpen)}
      >
        {companyFilter !== undefined ? companyName(companyFilter) : 'All properties'}
        <ChevronDown class="h-3.5 w-3.5" />
      </button>
      {#if companyMenuOpen}
        <button
          type="button"
          class="fixed inset-0 z-10 cursor-default"
          aria-label="Close company menu"
          onclick={() => (companyMenuOpen = false)}
        ></button>
        <div
          class="absolute left-0 top-full z-20 mt-1.5 w-64 max-h-72 overflow-y-auto rounded-xl border border-toyama-cloudy-gray bg-white p-1.5 shadow-[var(--tym-shadow-3)]"
        >
          <button
            type="button"
            class="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left hover:bg-toyama-light-carbon"
            onclick={() => {
              companyFilter = undefined;
              companyMenuOpen = false;
            }}
          >
            <span class="flex-1 text-[13px] font-semibold text-navy">All properties</span>
          </button>
          {#each companiesQuery.data?.results ?? [] as o (o.id)}
            <button
              type="button"
              class="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left hover:bg-toyama-light-carbon"
              onclick={() => {
                companyFilter = o.id;
                companyMenuOpen = false;
              }}
            >
              <span class="flex-1 truncate text-[13px] font-semibold text-navy">{o.name}</span>
              <span class="rounded-[5px] px-1.5 py-0.5 text-[10px] font-semibold capitalize" style="background:#F1F0EF; color:#666"
                >{o.kind}</span
              >
            </button>
          {/each}
        </div>
      {/if}
    </div>

    {#if companyFilter !== undefined && (unitFilterOptions.data?.results.length ?? 0) > 0}
      <div class="relative">
        <button
          type="button"
          class="tym-chip"
          class:active={unitFilter !== undefined}
          onclick={() => (unitMenuOpen = !unitMenuOpen)}
        >
          {unitFilter ?? 'All units'}
          <ChevronDown class="h-3.5 w-3.5" />
        </button>
        {#if unitMenuOpen}
          <button
            type="button"
            class="fixed inset-0 z-10 cursor-default"
            aria-label="Close unit menu"
            onclick={() => (unitMenuOpen = false)}
          ></button>
          <div
            class="absolute left-0 top-full z-20 mt-1.5 w-60 rounded-xl border border-toyama-cloudy-gray bg-white p-1.5 shadow-[var(--tym-shadow-3)]"
          >
            <button
              type="button"
              class="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left hover:bg-toyama-light-carbon"
              onclick={() => {
                unitFilter = undefined;
                unitMenuOpen = false;
              }}
            >
              <span class="flex-1 text-[13px] font-semibold text-navy">All units</span>
            </button>
            {#each unitFilterOptions.data?.results ?? [] as u (u)}
              <button
                type="button"
                class="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left hover:bg-toyama-light-carbon"
                onclick={() => {
                  unitFilter = u;
                  unitMenuOpen = false;
                }}
              >
                <span class="flex-1 truncate text-[13px] font-semibold text-navy">{u}</span>
              </button>
            {/each}
          </div>
        {/if}
      </div>
    {/if}

    {#if filtersActive}
      <button type="button" class="px-1 text-[12.5px] font-semibold text-toyama-teal underline underline-offset-2" onclick={clearFilters}
        >Reset</button
      >
    {/if}
    <span class="flex-1"></span>
    <span class="text-[12.5px] text-dark-gray"
      >{(view === 'board' ? boardQuery.data?.count : listData?.count) ?? 0} issues</span
    >
  </div>

  <div class="mb-3 flex items-center gap-2">
    <div class="relative max-w-sm flex-1">
      <input type="search" placeholder="Search issues…" bind:value={search} class="input" />
    </div>
    <div class="flex gap-[3px] rounded-[9px] border border-toyama-cloudy-gray bg-toyama-light-carbon p-[3px]">
      <button
        type="button"
        class="rounded-[7px] px-3.5 py-1.5 text-[12.5px] font-semibold {view === 'board'
          ? 'bg-navy text-white'
          : 'text-[#666]'}"
        onclick={() => (view = 'board')}
      >
        Board
      </button>
      <button
        type="button"
        class="rounded-[7px] px-3.5 py-1.5 text-[12.5px] font-semibold {view === 'list'
          ? 'bg-navy text-white'
          : 'text-[#666]'}"
        onclick={() => (view = 'list')}
      >
        List
      </button>
    </div>
  </div>

  <div class="mb-3 flex flex-wrap items-center gap-2" aria-label="Saved issue views">
    {#each savedViews as saved (saved.name)}
      <div class="flex items-center rounded-lg border bg-white"><button type="button" class="px-3 py-2 text-xs font-semibold" onclick={() => route.push({ tab: 'issues', filters: saved.filters })}>{saved.name}</button><button type="button" class="p-2 text-sm" aria-label={`Remove saved view ${saved.name}`} onclick={() => removeSavedView(saved.name)}>×</button></div>
    {/each}
    <input class="input !w-40" aria-label="Name this issue view" placeholder="View name" bind:value={savedViewName} maxlength="60" />
    <button type="button" class="btn-secondary" disabled={!savedViewName.trim()} onclick={saveView}>Save view</button>
    <button type="button" class="btn-secondary" onclick={async () => { try { await navigator.clipboard.writeText(location.href); toast.success('View link copied'); } catch { toast.error('Unable to copy. Copy the address from your browser.'); } }}>Copy view link</button>
    {#if status}<button type="button" class="btn-secondary" onclick={() => { status = ''; }}>Status: {statusLabel(status)} ×</button>{/if}
    {#if category}<button type="button" class="btn-secondary" onclick={() => { category = ''; }}>{categories.find(c => c.value === category)?.label} ×</button>{/if}
    {#if mine}<button type="button" class="btn-secondary" onclick={() => { mine = false; }}>Mine ×</button>{/if}
    {#if overdue}<button type="button" class="btn-secondary" onclick={() => { overdue = false; }}>Overdue ×</button>{/if}
  </div>

  {#if view === 'list'}
    <div class="mb-3 flex flex-wrap items-center gap-2 text-xs text-dark-gray">
      <label for="issue-sort">Sort all matches</label><select id="issue-sort" class="input !w-auto" bind:value={sort} disabled={bulkMutation.isPending}>
        <option value="id_desc">Newest issue first</option><option value="id_asc">Oldest issue first</option><option value="title_asc">Title A–Z</option><option value="title_desc">Title Z–A</option><option value="company_asc">Company A–Z</option><option value="company_desc">Company Z–A</option><option value="status_asc">Status A–Z</option><option value="status_desc">Status Z–A</option><option value="assignee_asc">Assignee A–Z</option><option value="assignee_desc">Assignee Z–A</option><option value="updated_desc">Recently updated</option><option value="updated_asc">Least recently updated</option><option value="next_action_asc">Next action soonest</option><option value="next_action_desc">Next action latest</option>
      </select>
      {#if sort !== 'id_desc'}<span>Full-result sorting loads up to 2,000 matching issues. Narrow filters for larger queues.</span>{/if}
      {#if !isIssuesOnly && listData?.results.length}<button type="button" class="btn-secondary" disabled={bulkMutation.isPending} onclick={() => {
        const rows = listData?.results ?? []; const allSelected = rows.every(row => selection.has(row.id)); const next = new Map(selection);
        for (const row of rows) { if (allSelected) next.delete(row.id); else next.set(row.id, row); } selection = next;
      }}>{listData.results.every(row => selection.has(row.id)) ? 'Deselect page' : 'Select page'}</button>{/if}
    </div>
  {/if}
  {#if !isIssuesOnly && selection.size}
    <form class="tym-card mb-4 shrink-0 space-y-3 p-4" onsubmit={e => { e.preventDefault(); applyBulk(); }} aria-label="Update selected issues">
      <fieldset disabled={bulkMutation.isPending || bulkConfirming} class="space-y-3">
        <div class="flex items-center justify-between gap-2"><h3 class="font-semibold text-navy">{selection.size} selected across loaded pages</h3><button type="button" class="btn-secondary" onclick={() => { selection = new Map(); }}>Clear selection</button></div>
        <label class="label" for="bulk-issue-action">Action</label><select id="bulk-issue-action" class="input" bind:value={bulkMode}><option value="assign">Assign / clear assignment</option><option value="next">Set next action and date</option><option value="status">Change status</option></select>
        {#if bulkMode === 'assign'}
          <label class="label" for="bulk-issue-assignee">Assignee (must have access to every selected team)</label><select id="bulk-issue-assignee" class="input" bind:value={bulkAssignee}><option value={null}>Unassigned — clear assignment</option>{#each commonAssignees as user (user.id)}<option value={user.id}>{user.name}</option>{/each}</select>
          {#if assignableQuery.isError}<p role="alert">{getErrorMessage(assignableQuery.error)}</p><button type="button" class="btn-secondary" onclick={() => assignableQuery.refetch()}>Retry assignees</button>{/if}
        {:else if bulkMode === 'next'}
          <label class="label" for="bulk-issue-next">Next action</label><input id="bulk-issue-next" class="input" bind:value={bulkNextAction} required />
          <label class="label" for="bulk-issue-date">Next action date</label><input id="bulk-issue-date" class="input" type="date" bind:value={bulkNextDate} required />
        {:else}
          <label class="label" for="bulk-issue-status">Status</label><select id="bulk-issue-status" class="input" bind:value={bulkStatus}>{#each statusDefs.filter(s => s.value) as item}<option value={item.value}>{item.label}</option>{/each}</select>
          {#if bulkStatus === 'resolved'}
            <p class="text-sm text-amber-800">Resolve together only when the same technician and resolution evidence apply. Customer confirmation remains separate.</p>
            <label class="label" for="bulk-issue-technician">Resolving technician</label><input id="bulk-issue-technician" class="input" bind:value={bulkTechnician} />
            {#if selectedRows.some(row => row.category === 'issue' && row.status !== 'resolved')}
              <label class="label" for="bulk-issue-root">Root cause</label><textarea id="bulk-issue-root" class="input" bind:value={bulkRoot}></textarea>
              <label class="label" for="bulk-issue-corrective">Corrective action</label><textarea id="bulk-issue-corrective" class="input" bind:value={bulkCorrective}></textarea>
              <label class="label" for="bulk-issue-preventive">Preventive action</label><textarea id="bulk-issue-preventive" class="input" bind:value={bulkPreventive}></textarea>
            {/if}
          {/if}
        {/if}
        {#if bulkError}<p role="alert" class="text-sm text-red-700">{bulkError}</p>{/if}
        <button type="submit" class="btn-primary">{bulkMutation.isPending ? 'Updating individually…' : `Apply to ${selection.size} selected issues`}</button>
      </fieldset>
      <p class="text-xs text-dark-gray">Updates are separate requests, not an atomic batch. Failed records stay selected; successful updates are not rolled back.</p>
    </form>
  {/if}
  {#if bulkResult}
    <div class="tym-card mb-4 shrink-0 space-y-2 p-4 text-sm" role="status">
      <p class="font-semibold">{bulkResult.succeeded.length} updated · {bulkResult.failed.length} failed</p>
      {#each bulkResult.failed as failure (failure.id)}<p><button type="button" class="text-toyama-teal underline" onclick={() => route.push({ tab: 'issues', id: failure.id })}>Issue #{failure.id}</button>: {failure.message}</p>{/each}
      {#if bulkResult.failed.length}<p>Review the errors, correct the action or selection, then apply again to retry only the still-selected issues. If a case changed teams, deselect it and reselect its refreshed row before retrying.</p>{/if}
      <button type="button" class="btn-secondary" onclick={() => { bulkResult = null; }}>Dismiss results</button>
    </div>
  {/if}

  {#snippet issueBoardCard(row: Issue)}
    {@const line2 = propertyLine(row)}
    <div
      class="kanban-card {dragId === row.id ? 'dragging' : ''}"
      role="button"
      draggable={!isIssuesOnly}
      ondragstart={() => onDragStart(row.id)}
      ondragend={onDragEnd}
      onclick={() => openDetail(row)}
      onkeydown={(e) => e.key === 'Enter' && openDetail(row)}
      tabindex="0"
    >
      <div class="mb-[3px] font-heading text-[13.5px] font-bold leading-tight text-navy">
        {row.title}
      </div>
      {@render followupSummary(row)}
      {#if row.company_name || line2}
        <div class="mb-2.5 text-[11.5px] text-dark-gray">
          {row.company_name}
          {#if line2}<div class="mt-0.5 text-[11px] text-[#999]">{line2}</div>{/if}
        </div>
      {/if}
      <div class="flex items-center justify-between">
        {#if row.assignee_user_id}
          <span class="inline-flex items-center gap-[7px]">
            <span
              class="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full font-heading text-[10px] font-bold"
              style="background:#E3F0F1; color:#226A73"
            >
              {initialsOf(assigneeName(row.assignee_user_id))}
            </span>
            <span class="text-[11.5px] text-[#666]">{assigneeName(row.assignee_user_id)}</span>
          </span>
        {:else}
          <span></span>
        {/if}
        <span class="inline-flex items-center gap-[5px] text-[11px] text-dark-gray">
          <Clock class="h-[13px] w-[13px]" />
          {timeAgo(row.created_at)}
        </span>
      </div>
    </div>
  {/snippet}

  {#snippet followupSummary(row: Issue)}
    <div class="mt-1 text-[11.5px] text-dark-gray">{categories.find((c) => c.value === row.category)?.label ?? 'Issue'}</div>
    {#if row.customer_satisfaction}<div class="text-[11.5px] text-toyama-teal">Customer: {row.customer_satisfaction === 'pending' ? 'Pending confirmation' : row.customer_satisfaction === 'satisfied' ? 'Satisfied' : 'Not satisfied'}</div>{/if}
    {#if row.status !== 'resolved' && (row.next_action || row.next_action_date)}
      <div class="my-1 text-[12px] text-toyama-teal">
        {row.next_action || 'Follow-up scheduled'}{row.next_action_date ? ` · Due ${dueLabel(row.next_action_date)}` : ''}
        {#if row.next_action_date && dueBadge(row.next_action_date)}<span class="ml-1 rounded bg-[#FBF0DC] px-1.5 py-0.5 text-[11px] font-bold text-[#8a5c00]">{dueBadge(row.next_action_date)}</span>{/if}
      </div>
    {/if}
  {/snippet}

  {#if view === 'board'}
    {#if boardQuery.isLoading}
      <p class="tym-loading-state">Loading board&hellip;</p>
    {:else if boardQuery.isError}
      <p class="tym-error-state">{getErrorMessage(boardQuery.error)}</p>
    {:else}
      <!-- mobile: chip row picks the status, one full-width column in focus -->
      <div class="flex min-h-0 flex-1 flex-col sm:hidden">
        <FilterChipRow
          chips={focusChips}
          activeId={focusStatus}
          onSelect={(id) => (focusStatusPick = id as IssueStatus)}
          ariaLabel="Focus an issue status"
          class="mb-2.5 shrink-0"
        />
        {#if focusColumn}
          {@const badge = statusBadge(focusColumn.status)}
          <div class="kanban-col kanban-col-focus" role="list">
            <div class="flex items-center gap-2 px-3.5 pb-2.5 pt-3.5">
              <span class="h-2.5 w-2.5 shrink-0 rounded-full" style="background:{badge.fg}"></span>
              <span class="font-heading text-[15px] font-bold text-navy">{statusLabel(focusColumn.status)}</span>
              <span class="font-heading text-[12px] font-semibold text-dark-gray">{focusColumn.cards.length}</span>
            </div>
            <div class="flex flex-1 flex-col gap-2.5 overflow-y-auto px-2.5 pb-20 pt-0.5">
              {#each focusColumn.cards as row (row.id)}
                {@render issueBoardCard(row)}
              {:else}
                <div class="px-1 py-3 text-center text-[12px] text-dark-gray">Nothing here yet. Try another status or clear your filters.</div>
              {/each}
            </div>
          </div>
        {/if}
      </div>

      <!-- desktop board: all three statuses side by side, drag to move -->
      <div class="hidden min-h-0 flex-1 gap-3.5 overflow-x-auto pb-2 sm:flex [scroll-snap-type:x_proximity]">
        {#each boardColumns as col (col.status)}
          {@const badge = statusBadge(col.status)}
          <div
            class="kanban-col"
            role="list"
            ondragover={(e) => e.preventDefault()}
            ondrop={() => onDropStatus(col.status)}
          >
            <div class="flex items-center gap-2 px-3.5 pb-2.5 pt-3.5">
              <span class="h-2 w-2 shrink-0 rounded-full" style="background:{badge.fg}"></span>
              <span class="font-heading text-[13px] font-bold text-navy">{statusLabel(col.status)}</span>
              <span class="font-heading text-[11px] font-semibold text-dark-gray">{col.cards.length}</span>
            </div>
            <div class="flex flex-1 flex-col gap-2.5 overflow-y-auto px-2.5 pb-3 pt-0.5">
              {#each col.cards as row (row.id)}
                {@render issueBoardCard(row)}
              {:else}
                <div class="px-1 py-3 text-center text-[12px] text-dark-gray">Nothing here yet. Open a record to update its status, or clear your filters.</div>
              {/each}
            </div>
          </div>
        {/each}
      </div>
    {/if}
  {:else if listLoading}
    <div class="tym-table-wrap shrink-0 overflow-hidden py-1" aria-busy="true" aria-label="Loading issues">
      {#each Array(6) as _, i (i)}
        <div class="flex items-center gap-5 border-b border-[#F5F5F4] px-[18px] py-[15px] last:border-none">
          <div class="flex-1">
            <div class="tym-shimmer h-3.5" style="width:{60 - i * 4}%"></div>
            <div class="tym-shimmer mt-2 h-2.5" style="width:{30 + i * 3}%"></div>
          </div>
          <div class="tym-shimmer h-[22px] w-[92px] shrink-0 rounded-pill"></div>
          <div class="tym-shimmer h-2.5 w-[110px] shrink-0"></div>
        </div>
      {/each}
    </div>
  {:else if listError}
    <p class="tym-error-state">{getErrorMessage(listError)} <button type="button" class="btn-secondary" onclick={retryList}>Retry</button></p>
  {:else if listData && listData.results.length === 0}
    <div class="tym-card tym-empty-panel flex-1">
      <span class="tym-empty-icon"><CircleAlert class="h-6 w-6" /></span>
      <div class="tym-empty-title">
        {filtersActive || search ? 'No issues match these filters' : 'No issues logged yet'}
      </div>
      <div class="tym-empty-hint">
        {filtersActive || search
          ? 'Try clearing a filter or searching for a different customer or title.'
          : 'Track an issue, customer feedback or conversation here, then assign the next follow-up.'}
      </div>
      {#if filtersActive || search}
        <button
          type="button"
          class="btn-secondary mt-[18px]"
          onclick={() => {
            clearFilters();
            search = '';
          }}>Clear filters</button
        >
      {:else}
        <button type="button" class="btn-secondary mt-[18px]" onclick={() => openCreate()}>Report issue</button>
      {/if}
    </div>
  {:else if listData}
    <!-- desktop/tablet table -->
    <!-- ponytail: `shrink-0`, not `flex-1` — the page shell is `h-full
         flex-col` (this page's own Board view above, mirroring DealsPage's
         Kanban, is what needs that height lock; this list branch doesn't),
         and flex items default to flex-shrink:1, so
         without shrink-0 the browser squeezes this wrap down to whatever
         space is left in that fixed-height column. .tym-table-wrap is
         `overflow: hidden` (app.css, for rounded corners), so a squeezed
         wrap silently clips every row past the fold with no scrollbar.
         shrink-0 keeps it at its natural (full row-count) height, which
         overflows .tym-page and lets <main class="overflow-y-auto">
         (Header.svelte) — the same container every other entity list page
         scrolls through — do the scrolling instead. -->
    <div class="tym-table-wrap tym-shadow hidden shrink-0 sm:block">
      <div class="tym-table-scroll">
        <table class="tym-table tym-dense">
          <thead>
            <tr>
              {#if !isIssuesOnly}<th scope="col"><span class="sr-only">Select issue</span></th>{/if}
              <th aria-sort={sortAria('title')}><button type="button" onclick={() => setSort('title')}>Issue ↕</button></th>
              <th aria-sort={sortAria('company')} style="width:190px"><button type="button" onclick={() => setSort('company')}>Company / Property ↕</button></th>
              <th aria-sort={sortAria('status')} style="width:130px"><button type="button" onclick={() => setSort('status')}>Status ↕</button></th>
              <th aria-sort={sortAria('assignee')} style="width:150px"><button type="button" onclick={() => setSort('assignee')}>Assignee ↕</button></th>
              <th aria-sort={sortAria('next_action')}><button type="button" onclick={() => setSort('next_action')}>Next action ↕</button></th>
              <th aria-sort={sortAria('updated')} class="num" style="width:96px"><button type="button" onclick={() => setSort('updated')}>Updated ↕</button></th>
            </tr>
          </thead>
          <tbody>
            {#each listData.results as row (row.id)}
              {@const badge = statusBadge(row.status)}
              {@const vl = vertLabel(row.vertical_id)}
              {@const line2 = propertyLine(row)}
              <tr class="clickable" onclick={() => openDetail(row)}>
                {#if !isIssuesOnly}<td><input type="checkbox" aria-label={`Select issue #${row.id}`} checked={selection.has(row.id)} disabled={bulkMutation.isPending} onclick={e => e.stopPropagation()} onchange={() => toggleSelection(row)} /></td>{/if}
                <td>
                  <div class="flex items-baseline gap-2">
                    <span class="shrink-0 font-heading text-[11px] font-bold tracking-[.03em]" style="color:#B9BDBE"
                      >#{row.id}</span
                    >
                    <span class="text-[13.5px] font-semibold leading-[1.35] text-navy">{row.title}</span>
                  </div>
                  {@render followupSummary(row)}
                  {#if row.order_number}<TcsNumber value={row.order_number} />{/if}
                  <div class="mt-1.5 flex flex-wrap items-center gap-2">
                    {#if row.unit}
                      <span
                        class="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold"
                        style="background:#E3F0F1; color:#226A73"
                        ><MapPin class="h-[11px] w-[11px]" />{row.unit}</span
                      >
                    {/if}
                    {#if row.room}<span class="text-[11.5px] text-[#666]">{row.room}</span>{/if}
                    {#if row.reporter_name}<span class="text-[11.5px] text-[#666]">{row.reporter_name}</span>{/if}
                    {#if row.source === 'wizhom'}
                      <span class="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10.5px] font-bold" style="background:#E6E9EE; color:#10223A"
                        >App</span
                      >
                    {/if}
                  </div>
                </td>
                <td>
                  <div class="text-[13px] font-medium text-heavy-carbon">{row.company_name ?? ''}</div>
                  {#if line2}<div class="mt-0.5 text-[11px] text-dark-gray">{line2}</div>{/if}
                  <div class="mt-0.5 inline-flex items-center gap-1.5 text-[11px] text-dark-gray">
                    <span class="h-1.5 w-1.5 rounded-full" style="background:{vl.dot}"></span>{vl.name}
                  </div>
                </td>
                <td>
                  <span class="inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-[11.5px] font-bold whitespace-nowrap" style="background:{badge.bg}; color:{badge.fg}">
                    <span class="h-1.5 w-1.5 rounded-full" style="background:{badge.fg}"></span>{statusLabel(row.status)}
                  </span>
                </td>
                <td>
                  {#if row.assignee_user_id}
                    <span class="inline-flex items-center gap-2">
                      <span
                        class="flex h-[26px] w-[26px] items-center justify-center rounded-full font-heading text-[10px] font-bold"
                        style="background:#E3F0F1; color:#226A73"
                        >{initialsOf(assigneeName(row.assignee_user_id))}</span
                      >
                      <span class="text-[12.5px] text-heavy-carbon">{assigneeName(row.assignee_user_id)}</span>
                    </span>
                  {/if}
                </td>
                <td class="text-xs text-dark-gray">{row.next_action_date?.slice(0, 10) || '—'}</td>
                <td class="num whitespace-nowrap text-[12.5px] text-dark-gray" title={formatIST(row.updated_at)}>{relativeTime(row.updated_at)}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </div>

    <!-- mobile card list -->
    <div class="flex flex-1 flex-col gap-2.5 sm:hidden">
      {#each listData.results as row (row.id)}
        {@const badge = statusBadge(row.status)}
        <div role="button" tabindex="0" class="issue-card" onclick={() => openDetail(row)} onkeydown={e => { if (e.key === 'Enter') openDetail(row); }}>
          <div class="flex items-center gap-1.5">
            {#if !isIssuesOnly}<input type="checkbox" aria-label={`Select issue #${row.id}`} checked={selection.has(row.id)} disabled={bulkMutation.isPending} onclick={e => e.stopPropagation()} onkeydown={e => e.stopPropagation()} onchange={() => toggleSelection(row)} />{/if}
            <span class="inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-[11px] font-bold" style="background:{badge.bg}; color:{badge.fg}">
              <span class="h-1.5 w-1.5 rounded-full" style="background:{badge.fg}"></span>{statusLabel(row.status)}
            </span>
            {#if row.source === 'wizhom'}
              <span class="rounded-md px-2 py-0.5 text-[10px] font-bold" style="background:#E6E9EE; color:#10223A">App</span>
            {/if}
            <span class="flex-1"></span>
            <span class="tym-tabular text-[11px] text-dark-gray" title={formatIST(row.updated_at)}>{relativeTime(row.updated_at)}</span>
          </div>
          <div class="mt-2 text-[15px] font-semibold leading-[1.35] text-navy">{row.title}</div>
          {#if row.order_number}<TcsNumber value={row.order_number} />{/if}
          {@render followupSummary(row)}
          <div class="mt-2 flex flex-wrap items-center gap-1.5">
            {#if row.unit}
              <span class="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold" style="background:#E3F0F1; color:#226A73"
                >{row.unit}</span
              >
            {/if}
            {#if row.room}<span class="text-[11.5px] text-[#666]">{row.room}</span>{/if}
            {#if row.location}<span class="text-[11.5px] text-[#666]">{row.location}</span>{/if}
            <span class="text-[11.5px] text-dark-gray">{row.company_name ?? ''}</span>
          </div>
        </div>
      {/each}
    </div>

    <Pagination
      page={listData.page}
      perPage={listData.per_page}
      count={listData.count}
      onChange={(p) => (page = p)}
    />
  {/if}

  <!-- floating "report issue" affordance on mobile, matching the desktop header's + New -->
  <button type="button" class="tym-fab flex items-center sm:hidden" onclick={() => openCreate()}>
    <Plus class="h-5 w-5" />
    Report issue
  </button>
</div>

{#if selectedIssueId != null}
  <IssueDetailPanel
    issueId={selectedIssueId}
    assignableUsers={assignableQuery.data?.results ?? []}
    autoResolve={boardResolveArm === selectedIssueId}
    onArmConsumed={() => (boardResolveArm = null)}
    onClose={closeDetail}
    onEdit={(issue) => {
      closeDetail();
      openEdit(issue);
    }}
  />
{/if}

{#if showForm}
  <Modal title={editingId ? 'Edit customer record' : 'New issue, feedback or conversation'} onClose={closeForm} wide>
    <form id="issue-form" class="space-y-4" onsubmit={submit}>
      <h3 class="font-heading text-sm font-bold text-navy">Customer &amp; location</h3>
      {#if supportTeams.length > 1}
        <div>
          <label class="label" for="vertical">Support team</label>
          <select id="vertical" class="input" bind:value={form.vertical_id} disabled={editingId !== null} onchange={() => { form.company_id = undefined; form.person_id = undefined; form.assignee_user_id = undefined; editingCompanyName = undefined; selectedCompanyKind = undefined; }}>
            {#each supportTeams as v (v.id)}
              <option value={v.id}>{v.name}</option>
            {/each}
          </select>
        </div>
      {/if}
      <div>
        <label class="label" for="company">Company / property</label>
        {#if hasCRMAccess(form.vertical_id)}
        <CompanyCombobox
          id="company"
          bind:value={form.company_id}
          bind:selectedKind={selectedCompanyKind}
          companies={formCompanies}
          verticalId={form.vertical_id}
          defaultKind={formDefaultCompanyKind}
           placeholder="Search or add a company…"
        />
        {:else}
          <select id="company" class="input" bind:value={form.company_id}>
            <option value={undefined}>No linked company</option>
            {#each formCompanies as c (c.id)}<option value={c.id}>{c.name}</option>{/each}
          </select>
          <p class="mt-1 text-xs text-dark-gray">Support-only team: choose an existing company or leave blank. Company search and creation need CRM access; use site/address and customer details below.</p>
        {/if}
      </div>
      <div>
        <label class="label" for="site-type">Site type (optional)</label>
        <select id="site-type" class="input" bind:value={form.site_type}>
          <option value="">Not specified</option><option value="hotel">Hotel</option><option value="home">Home</option><option value="office">Office</option><option value="commercial">Commercial</option><option value="other">Other</option>
        </select>
      </div>
      <div>
        <label class="label" for="issue-location">Site / address (optional)</label>
        <input id="issue-location" class="input" bind:value={form.location} placeholder="Site name, address or directions" />
      </div>
      <div>
        <label class="label" for="unit">Building / block / unit (optional)</label>
        <p class="mb-1.5 text-[11.5px] text-dark-gray">
          Add any building, block or unit context that helps locate the problem.
        </p>
        <UnitCombobox
          id="unit"
          bind:value={form.unit}
          suggestions={formUnitsQuery.data?.results ?? []}
        />
      </div>
      <div>
        <label class="label" for="room">Room / flat / area (optional)</label>
        <input id="room" class="input" bind:value={form.room} placeholder="e.g. 204 or 204, 409" />
      </div>
      <h3 class="border-t border-toyama-cloudy-gray pt-4 font-heading text-sm font-bold text-navy">What happened?</h3>
      <div>
        <label class="label" for="title">Title</label>
        <input id="title" class="input" bind:value={form.title} placeholder="e.g. Customer needs help with a bedroom dimmer" required />
      </div>
      {#if !isIssuesOnly}
        <div>
          <label class="label" for="issue-category">Category</label>
          <select id="issue-category" class="input" bind:value={form.category}>
            {#each categories as c}<option value={c.value}>{c.label}</option>{/each}
          </select>
          <p class="mt-1 text-xs text-dark-gray">{form.category === 'issue' ? 'A problem to investigate and fix. Resolution requires cause, corrective action and prevention (CAPA).' : form.category === 'customer_feedback' ? 'A suggestion, compliment or feedback to review. Close when handled; no CAPA required.' : 'Record a call, visit or message and any follow-up. Close when complete; no CAPA required.'}</p>
        </div>
        <h3 class="border-t border-toyama-cloudy-gray pt-4 font-heading text-sm font-bold text-navy">Plan the follow-up</h3>
        <div>
          <label class="label" for="next-action">Next action</label>
          <input id="next-action" class="input" bind:value={form.next_action} placeholder="e.g. Call the customer to confirm the fix" />
        </div>
        <div>
          <label class="label" for="next-action-date">Follow-up date</label>
          <input id="next-action-date" type="date" class="input" bind:value={form.next_action_date} />
        </div>
        <div>
          <label class="label" for="assignee">Assigned to</label>
          <select id="assignee" class="input" bind:value={form.assignee_user_id}>
            <option value={undefined}>Unassigned</option>
            {#each formAssignableQuery.data?.results ?? [] as u (u.id)}
              <option value={u.id}>{u.name}</option>
            {/each}
          </select>
        </div>
        <h3 class="border-t border-toyama-cloudy-gray pt-4 font-heading text-sm font-bold text-navy">Customer contact details</h3>
        <div>
          <label class="label" for="reporter-name">Customer / reporter name</label>
          <input id="reporter-name" class="input" bind:value={form.reporter_name} placeholder="Who shared this with us?" />
        </div>
        <div>
          <label class="label" for="reporter-phone">Customer phone</label>
          <input id="reporter-phone" type="tel" class="input" bind:value={form.reporter_phone} placeholder="Customer's contact number" />
        </div>
        <div>
          <label class="label" for="reporter-email">Customer email</label>
          <input id="reporter-email" type="email" class="input" bind:value={form.reporter_email} placeholder="name@company.com" />
        </div>
      {/if}
      {#if !isIssuesOnly}
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {#if hasCRMAccess(form.vertical_id)}
          <div>
            <label class="label" for="person">Linked customer contact</label>
            <select id="person" class="input" bind:value={form.person_id}>
              <option value={undefined}>-- none --</option>
              {#each peopleQuery.data?.results ?? [] as p (p.id)}
                <option value={p.id}>{p.first_name} {p.last_name}</option>
              {/each}
            </select>
          </div>
          {/if}
        </div>
        <h3 class="border-t border-toyama-cloudy-gray pt-4 font-heading text-sm font-bold text-navy">Progress</h3>
        <div>
          <label class="label" for="status">Status</label>
          <select id="status" class="input" bind:value={form.status}>
            <option value="open">Open</option>
            <option value="in_progress">In progress</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>
      {/if}
      {#if !isIssuesOnly && form.status === 'resolved'}
        <div>
          <label class="label" for="form-resolving-technician">Resolving technician name</label>
          <input id="form-resolving-technician" class="input" bind:value={form.resolving_technician_name} required={requiresResolution(originalStatus, form.status)} />
          <p class="mt-1 text-xs text-dark-gray">{resolutionHint(supportTeams.find(team => team.id === form.vertical_id)?.name)}</p>
        </div>
      {/if}
      {#if !isIssuesOnly && form.category === 'issue' && form.status === 'resolved'}
        <div>
          <label class="label" for="form-root-cause">Root cause (CAPA)</label>
          <textarea id="form-root-cause" class="input" bind:value={form.capa_root_cause}></textarea>
        </div>
        <div>
          <label class="label" for="form-corrective-action">Corrective action (CAPA)</label>
          <textarea id="form-corrective-action" class="input" bind:value={form.capa_corrective_action}></textarea>
        </div>
        <div>
          <label class="label" for="form-preventive-action">Preventive action (CAPA)</label>
          <textarea id="form-preventive-action" class="input" bind:value={form.capa_preventive_action}></textarea>
        </div>
      {/if}
      <div>
        <label class="label" for="body">Details (optional)</label>
        <textarea id="body" rows="5" class="input" bind:value={form.body} placeholder="Describe the conversation or problem, what the customer needs, and what has already been tried."></textarea>
      </div>
      <div>
        <span class="label">Photos (up to 10MB each)</span>
        <div class="flex flex-wrap gap-2.5">
          {#each stagedPhotos as p, i (p.previewUrl)}
            <div class="relative h-16 w-[84px]">
              <img src={p.previewUrl} alt="Attached" class="block h-full w-full rounded-[9px] border border-toyama-cloudy-gray object-cover" />
              <button
                type="button"
                class="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-navy text-white"
                onclick={() => removeStagedPhoto(i)}
                aria-label="Remove photo"
              >
                <X class="h-2.5 w-2.5" />
              </button>
            </div>
          {/each}
          <input type="file" accept="image/*" class="hidden" bind:this={photoInput} onchange={onPhotoChosen} />
          <button
            type="button"
            class="flex h-16 w-[84px] flex-col items-center justify-center gap-0.5 rounded-[9px] border-[1.5px] border-dashed border-[#C9CDCE] bg-[#FBFBFA] text-[10.5px] font-semibold text-dark-gray hover:border-toyama-teal hover:text-toyama-teal"
            onclick={() => photoInput?.click()}
          >
            <Camera class="h-[17px] w-[17px]" />
            Add
          </button>
        </div>
      </div>

      <CustomFieldsForm defs={customFieldDefs} values={form.custom} />

      {#if formError}<p class="text-sm text-red-600">{formError}</p>{/if}
    </form>

    {#snippet footer()}
      <div class="flex justify-end gap-2">
        <button type="button" class="btn-secondary" onclick={closeForm}>Cancel</button>
        <!-- sibling of the <form>, so it submits via the `form` attribute -->
        <button type="submit" form="issue-form" class="btn-primary" disabled={saveMutation.isPending}>
          {saveMutation.isPending ? 'Saving…' : editingId ? 'Save changes' : 'Create record'}
        </button>
      </div>
    {/snippet}
  </Modal>
{/if}
